import { useEffect, useMemo, useState } from "react";
import { vocabularize } from "src/Common/vocab";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import OutsideClickHandler from "react-outside-click-handler/build/OutsideClickHandler";
import moment from "moment";
import { can, handleError } from "src/Common/function";
import PolicyService from "src/Services/API/policy.service";
import ConfirmDialog from "src/Common/confirmdialog";
import Loader from "src/Components/Common/Loader";
import PolicyWizardModal from "./PolicyWizardModal";

const PAGE_SIZE = 9;

const TYPE_BADGE = {
  system: "bg-[#EEF0F9] text-[#5F6477]",
  operational: "bg-[#E9F8EF] text-[#1A9E5A]",
  integration: "bg-[#FFEDD5] text-[#C2410C]",
  custom: "bg-[#F3E8FF] text-[#7E22CE]",
  restricted: "bg-[#FDEAEA] text-[#D93A3A]",
};

// Filter tabs mirror the policy types (plus "All").
const TABS = ["all", "operational", "integration", "custom", "restricted"];
const TAB_LABEL = {
  all: "All Policies",
  operational: "Operational",
  integration: "Integration",
  custom: "Custom",
  restricted: "Restricted",
};

const PlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M8 2.83334C8.32217 2.83334 8.58334 3.09451 8.58334 3.41668V7.41668H12.5833C12.9055 7.41668 13.1667 7.67784 13.1667 8.00001C13.1667 8.32218 12.9055 8.58334 12.5833 8.58334H8.58334V12.5833C8.58334 12.9055 8.32217 13.1667 8 13.1667C7.67783 13.1667 7.41667 12.9055 7.41667 12.5833V8.58334H3.41667C3.0945 8.58334 2.83334 8.32218 2.83334 8.00001C2.83334 7.67784 3.0945 7.41668 3.41667 7.41668H7.41667V3.41668C7.41667 3.09451 7.67783 2.83334 8 2.83334Z"
      fill="white"
    />
  </svg>
);

const DotsVerticalIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M8 4.5C8.82843 4.5 9.5 3.82843 9.5 3C9.5 2.17157 8.82843 1.5 8 1.5C7.17157 1.5 6.5 2.17157 6.5 3C6.5 3.82843 7.17157 4.5 8 4.5ZM8 9.5C8.82843 9.5 9.5 8.82843 9.5 8C9.5 7.17157 8.82843 6.5 8 6.5C7.17157 6.5 6.5 7.17157 6.5 8C6.5 8.82843 7.17157 9.5 8 9.5ZM9.5 13C9.5 13.8284 8.82843 14.5 8 14.5C7.17157 14.5 6.5 13.8284 6.5 13C6.5 12.1716 7.17157 11.5 8 11.5C8.82843 11.5 9.5 12.1716 9.5 13Z"
      fill="currentColor"
    />
  </svg>
);

const EditIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M11.3333 2.00004C11.5085 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6667 1.44775C12.9143 1.44775 13.1595 1.49653 13.3883 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5 13.6667L1.33333 14.6667L2.33333 11L11.3333 2.00004Z"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M2 4H14M12.6667 4V13.3333C12.6667 14 12 14.6667 11.3333 14.6667H4.66667C4 14.6667 3.33333 14 3.33333 13.3333V4M5.33333 4V2.66667C5.33333 2 6 1.33333 6.66667 1.33333H9.33333C10 1.33333 10.6667 2 10.6667 2.66667V4M6.66667 7.33333V11.3333M9.33333 7.33333V11.3333"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Policies list — the IAM "Policies" page. Type-filter tabs, the roles using
 * each policy, last-updated, a row action menu and pagination. (Branch scope is
 * not part of the model, so that column from the mockup is omitted.)
 */
function Policies() {
  const navigate = useNavigate();
  const [policies, setPolicies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [page, setPage] = useState(1);
  const [menuOpenId, setMenuOpenId] = useState(null);
  // Create/Edit wizard modal state — editPolicyId null = create mode.
  const [wizardShown, setWizardShown] = useState(false);
  const [editPolicyId, setEditPolicyId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () => {
    setIsLoading(true);
    PolicyService.getAll()
      .then((res) => setPolicies(res.data ?? []))
      .catch(handleError)
      .finally(() => setIsLoading(false));
  };

  useEffect(load, []);

  const filtered = useMemo(
    () => (activeTab === "all" ? policies : policies.filter((p) => p.type === activeTab)),
    [policies, activeTab],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pagePolicies = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [totalPages, page]);

  const doDelete = (policy) => {
    setMenuOpenId(null);
    if ((policy.roles?.length ?? 0) > 0) {
      toast("Detach this policy from all roles before deleting it");
      return;
    }
    setDeleteTarget(policy);
  };

  const confirmDelete = () => {
    PolicyService.remove(deleteTarget.id)
      .then(() => {
        toast("Policy deleted");
        load();
      })
      .catch(handleError);
    setDeleteTarget(null);
  };

  return (
    <div className="px-7 py-6">
      {/* <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="text-[24px] font-OpenSansBold text-[#1A2332]">Policies</h1>
          <p className="text-[13px] font-OpenSansRegular text-[#868DA6] mt-1">
            A policy is a reusable bundle of permissions attached to roles.
          </p>
        </div>
      </div> */}

      <div className="bg-white rounded-[12px] outline outline-1 outline-offset-[-1px] outline-[#E5E8F3] shadow-[0px_1px_2px_rgba(0,0,0,0.06)] overflow-visible mt-4">
        {/* Tabs + Create */}
        <div className="flex items-end justify-between px-6 pt-4 border-b border-[#E5E8F3]">
          <div className="flex gap-6">
            {TABS.map((t) => (
              <button
                key={t}
                onClick={() => {
                  setActiveTab(t);
                  setPage(1);
                }}
                className={`pb-4 text-[14px] leading-5 tracking-[0.14px] capitalize font-OpenSansSemiBold border-b-2 -mb-[1px] ${
                  activeTab === t
                    ? "text-[#0058E5] border-[#0058E5]"
                    : "text-[#7C829A] border-transparent hover:text-[#1A2332]"
                }`}
              >
                {TAB_LABEL[t]}
              </button>
            ))}
          </div>
          {can("iam:createPolicy") && (
            <button
              onClick={() => {
                setEditPolicyId(null);
                setWizardShown(true);
              }}
              className="mb-4 h-8 px-3 rounded-[8px] bg-[#0065FD] text-white text-[14px] leading-5 tracking-[0.14px] capitalize font-OpenSansSemiBold hover:bg-[#0048bd] inline-flex items-center justify-center gap-2"
            >
              <PlusIcon />
              Create Policy
            </button>
          )}
        </div>

        {/* Header */}
        <div className="grid grid-cols-[2.2fr_1fr_1.6fr_1.2fr_40px] px-6 h-11 bg-[#F8F9FE] border-b border-[#E5E8F3] items-center">
          {["Policy Name", "Type", "Used By Roles", "Last Updated"].map((h) => (
            <span key={h} className="text-[12px] leading-[18px] font-OpenSansSemiBold text-[#5F6477]">
              {h}
            </span>
          ))}
          <span />
        </div>

        {isLoading ? (
          <Loader variant="block" className="px-6 py-10" ariaLabel="loading-policies" />
        ) : pagePolicies.length === 0 ? (
          <div className="px-6 py-10 text-center text-[#868DA6] font-OpenSansRegular">No policies found.</div>
        ) : (
          pagePolicies.map((policy) => {
            const roles = policy.roles ?? [];
            const shownRoles = roles.slice(0, 2);
            const extra = roles.length - shownRoles.length;
            return (
              <div
                key={policy.id}
                onClick={() => navigate(`/policies/${policy.id}`)}
                className="grid grid-cols-[2.2fr_1fr_1.6fr_1.2fr_40px] px-6 py-4 border-b border-[#E5E8F3] last:border-b-0 cursor-pointer hover:bg-[#F8F9FE] items-center"
              >
                <div>
                  <p className="text-[14px] leading-5 font-OpenSansSemiBold text-[#1A2332]">{vocabularize(policy.name)}</p>
                  {policy.description && (
                    <p className="text-[14px] leading-5 font-InterRegular text-[#525252]">{vocabularize(policy.description)}</p>
                  )}
                </div>
                <span>
                  <span
                    className={`inline-flex px-2 py-[2px] rounded-[6px] text-[12px] leading-[18px] tracking-[0.12px] font-OpenSansSemiBold capitalize ${
                      TYPE_BADGE[policy.type] || "bg-[#EEF0F9] text-[#5F6477]"
                    }`}
                  >
                    {policy.type}
                  </span>
                </span>
                <span className="flex items-center gap-1 flex-wrap">
                  {roles.length === 0 ? (
                    <span className="text-[14px] leading-5 font-OpenSansRegular text-[#9AA0AE]">—</span>
                  ) : (
                    <>
                      {shownRoles.map((r) => (
                        <span
                          key={r.id}
                          className="inline-flex px-2 py-[2px] rounded-[6px] bg-[#EAF3FF] text-[#0058E5] text-[12px] leading-[18px] tracking-[0.12px] font-OpenSansSemiBold capitalize"
                        >
                          {r.name}
                        </span>
                      ))}
                      {extra > 0 && (
                        <span className="inline-flex px-2 py-[2px] rounded-[6px] bg-[#EEF0F9] text-[#5F6477] text-[12px] leading-[18px] tracking-[0.12px] font-OpenSansSemiBold">
                          +{extra}
                        </span>
                      )}
                    </>
                  )}
                </span>
                <span className="text-[14px] leading-5 font-OpenSansRegular text-[#5F6477]">
                  {policy.updatedAt ? moment(policy.updatedAt).fromNow() : "—"}
                </span>
                <span className="relative flex justify-end" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => setMenuOpenId(menuOpenId === policy.id ? null : policy.id)}
                    className={`w-8 h-8 rounded-[8px] flex items-center justify-center transition-colors ${
                      menuOpenId === policy.id
                        ? "bg-[#EEF0F7] text-[#1A2332]"
                        : "text-[#868DA6] hover:bg-[#EEF0F7] hover:text-[#1A2332]"
                    }`}
                    title="Actions"
                  >
                    <DotsVerticalIcon />
                  </button>
                  {menuOpenId === policy.id && (
                    <OutsideClickHandler onOutsideClick={() => setMenuOpenId(null)}>
                      <div className="absolute right-0 top-9 z-20 w-[184px] bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-[#E5E8F3] shadow-[0px_8px_24px_rgba(16,24,40,0.12)] p-1">
                        {can("iam:updatePolicy") && (
                          <button
                            onClick={() => {
                              setMenuOpenId(null);
                              setEditPolicyId(policy.id);
                              setWizardShown(true);
                            }}
                            className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-[6px] text-[13px] leading-5 font-OpenSansMedium text-[#1A2332] hover:bg-[#F5F9FF] hover:text-[#0065FD]"
                          >
                            <EditIcon />
                            Edit
                          </button>
                        )}
                        {can("iam:deletePolicy") && (
                          <>
                            {can("iam:updatePolicy") && <div className="my-1 h-px bg-[#E5E8F3]" />}
                            <button
                              onClick={() => doDelete(policy)}
                              className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-[6px] text-[13px] leading-5 font-OpenSansMedium text-[#D93A3A] hover:bg-[#FDEAEA]"
                            >
                              <TrashIcon />
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </OutsideClickHandler>
                  )}
                </span>
              </div>
            );
          })
        )}

        {/* Pagination */}
        {!isLoading && filtered.length > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-[#E5E8F3]">
            <span className="text-[13px] font-OpenSansRegular text-[#868DA6]">
              Showing {(page - 1) * PAGE_SIZE + 1} to {Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}{" "}
              results
            </span>
            <div className="flex items-center gap-1">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1.5 rounded-[6px] border border-[#E5E8F3] text-[13px] font-OpenSansSemiBold text-[#404A6E] disabled:opacity-40"
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`w-8 h-8 rounded-[6px] text-[13px] font-OpenSansSemiBold ${
                    p === page ? "bg-[#0058E5] text-white" : "text-[#404A6E] hover:bg-[#F8F9FE]"
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="px-3 py-1.5 rounded-[6px] border border-[#E5E8F3] text-[13px] font-OpenSansSemiBold text-[#404A6E] disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <PolicyWizardModal
        shown={wizardShown}
        policyId={editPolicyId}
        onClose={() => setWizardShown(false)}
        onSaved={() => load()}
      />

      {deleteTarget && (
        <ConfirmDialog
          title="DELETE POLICY"
          description={`Are you sure you want to delete "${deleteTarget.name}"?`}
          shown={!!deleteTarget}
          onConfirmClicked={confirmDelete}
          onCancelClicked={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

export default Policies;
